import { useCallback, useEffect, useState } from "react";
import { Lightning } from "react-bootstrap-icons";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "state/hooks.ts";
import { fetchRealWallet, getRealWalletState } from "state/slices/user-real-wallet-slice.ts";
import { setWalletIsStale } from "state/slices/wallet-slice.ts";
import { postRequest } from "utils/httpClient.ts";
import { reportError } from "utils/report-error";
import { getLightningWithdrawalPath, getWithdrawalPreviewPath } from "utils/backend-path-builders.ts";
import { ErrorResponseBody } from "interfaces/error-response-body.ts";
import { ErrorCodes } from "interfaces/error-codes.ts";
import { sparketGold, sparketLightGray, WITHDRAWAL_FEE_MODE } from "utils/constants.ts";
import { getSpeedSession } from "utils/integration/integrated-app-tools.ts";
import { getSpeedIntegrationData } from "state/slices/integrated-app-slice.ts";
import { WithdrawalPreviewData } from "interfaces/withdrawal-preview.ts";

import AmountSelector from "components/commerce/amount-selector.tsx";
import GreyModalSection from "components/commerce/grey-modal-section.tsx";
import BlockyBoldText from "components/custom-texts/blocky-bold-text.tsx";
import BlockyLightText from "components/custom-texts/blocky-light-text.tsx";
import WithdrawalSummaryBlock from "./withdrawal-summary-block.tsx";

interface LightningWithdrawalBlockProps {
  onClose: () => void;
}

const LIGHTNING_MIN_WITHDRAWAL = 1;
// Speed's only LN-Address domain. Backend rejects anything else, so we hard-pin
// the suffix in the UI and let the user type only the username portion.
const SPEED_LN_SUFFIX = "@speed.app";

const LightningWithdrawalBlock = ({ onClose }: LightningWithdrawalBlockProps) => {
  const dispatch = useAppDispatch();
  const realWalletState = useAppSelector(getRealWalletState);

  const availableBalance = realWalletState.balance || 0;
  const isBalanceLoading = realWalletState.fetchStatusState === "loading";

  const speedData = useAppSelector(getSpeedIntegrationData);
  const sessionSpeed = getSpeedSession();
  const prefilledLightningAddress =
    (speedData?.lightningAddress && speedData.lightningAddress.trim().length > 0
      ? speedData.lightningAddress
      : sessionSpeed.lightningAddress) || "";
  const hasPrefilledAddress =
    !!prefilledLightningAddress && prefilledLightningAddress.trim().length > 0;

  const [amount, setAmount] = useState<number | undefined>(undefined);
  // Manual fallback: user types only the username portion (we add @speed.app
  // server-side and on submit). Empty when we already have a prefilled p_add.
  const [lightningUsername, setLightningUsername] = useState<string>("");
  const [processing, setProcessing] = useState(false);
  const [previewData, setPreviewData] = useState<WithdrawalPreviewData | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Sanitize: strip whitespace, accidental '@', and the trailing @speed.app
  // pasted by users who copy a full address into the username-only input.
  const cleanedUsername = lightningUsername
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "")
    .replace(/@.*$/, "");
  const manualAddress = cleanedUsername ? `${cleanedUsername}${SPEED_LN_SUFFIX}` : "";
  const effectiveAddress = hasPrefilledAddress
    ? prefilledLightningAddress
    : manualAddress;

  // Fetch withdrawal preview when amount changes
  useEffect(() => {
    const fetchPreview = async () => {
      if (amount && amount >= LIGHTNING_MIN_WITHDRAWAL) {
        setPreviewLoading(true);
        try {
          const response = await postRequest(getWithdrawalPreviewPath(true), {
            body: {
              amount,
              fee_mode: WITHDRAWAL_FEE_MODE.DEDUCT,
            },
          });
          setPreviewData(response as WithdrawalPreviewData);
        } catch (error) {
          console.error("Failed to fetch withdrawal preview:", error);
          // Fallback to client-side calculation
          const feeAmount = amount * 0.03;
          setPreviewData({
            fee_amount: feeAmount,
            amount_sent_to_user: amount - feeAmount,
            amount_deducted_from_balance: amount,
          });
        } finally {
          setPreviewLoading(false);
        }
      } else {
        setPreviewData(null);
        setPreviewLoading(false);
      }
    };

    fetchPreview();
  }, [amount]);

  const handleWithdraw = useCallback(async () => {
    if (!amount || amount < LIGHTNING_MIN_WITHDRAWAL) {
      toast.error(`Minimum withdrawal amount is $${LIGHTNING_MIN_WITHDRAWAL}.`);
      return;
    }
    if (!effectiveAddress || effectiveAddress.trim().length === 0) {
      toast.error("Please enter your Speed Lightning Address username.");
      return;
    }

    setProcessing(true);
    try {
      await postRequest(getLightningWithdrawalPath(), {
        body: {
          amount,
          // Only include the manually typed @speed.app fallback when the user
          // has no p_add on file; the backend ignores this field otherwise.
          ...(hasPrefilledAddress ? {} : { lightning_address: manualAddress }),
        },
      });

      toast.success("Withdrawal request submitted. It will be reviewed shortly.");
      dispatch(fetchRealWallet());
      dispatch(setWalletIsStale(true));
      onClose();
    } catch (error: any) {
      const errorResponse: ErrorResponseBody | undefined = error.response?.data;
      if (errorResponse) {
        const code = errorResponse.error_code;
        if (code === ErrorCodes.DepositLimitationExceeded) {
          toast.error(errorResponse.friendly_error_message);
          return;
        }
      }
      reportError(error, "withdrawal", { method: "speed_lightning", error_code: errorResponse?.error_code });
      toast.error("Something went wrong. Please contact support.");
    } finally {
      setProcessing(false);
    }
  }, [amount, effectiveAddress, hasPrefilledAddress, manualAddress, dispatch, onClose]);

  const disabled =
    processing ||
    !amount ||
    amount < LIGHTNING_MIN_WITHDRAWAL ||
    !effectiveAddress ||
    effectiveAddress.trim().length === 0;

  return (
    <GreyModalSection>
      <div className="d-flex align-items-center mb-3" style={{ gap: 8, color: "white" }}>
        <Lightning size={18} color={sparketGold} />
        <BlockyBoldText>Lightning withdrawal</BlockyBoldText>
      </div>

      <div className="mb-3">
        <AmountSelector
          presetAmounts={[{ amount: 1 }, { amount: 5 }, { amount: 20 }]}
          selectedAmount={amount || 0}
          setSelectedAmount={(a) => setAmount(a)}
          placeholder={`$${LIGHTNING_MIN_WITHDRAWAL} minimum`}
          disabled={isBalanceLoading || processing}
          maxAmount={availableBalance}
        />
        <div className="mt-2 text-center">
          <BlockyLightText style={{ color: sparketLightGray, fontSize: "0.85em" }}>
            Minimum withdrawal amount: ${LIGHTNING_MIN_WITHDRAWAL}
          </BlockyLightText>
        </div>
      </div>

      {hasPrefilledAddress ? (
        <div className="mb-2 text-center">
          <BlockyLightText style={{ color: sparketLightGray, fontSize: "0.8em" }}>
            Sending to your Speed Wallet:
          </BlockyLightText>
          <div className="mt-1">
            <BlockyBoldText style={{ color: "white", fontSize: "0.95em", wordBreak: "break-all" }}>
              {prefilledLightningAddress}
            </BlockyBoldText>
          </div>
        </div>
      ) : (
        <div className="mb-2">
          <div
            className="mb-2"
            style={{
              padding: "8px 12px",
              borderRadius: "6px",
              border: "1px solid rgba(255, 193, 7, 0.4)",
              background: "rgba(255, 193, 7, 0.08)",
            }}
          >
            <BlockyLightText style={{ color: "white", fontSize: "0.85em" }}>
              Lightning Address not configured. Set one up in your Speed Wallet,
              or enter it manually below.
            </BlockyLightText>
          </div>
          <BlockyLightText style={{ color: sparketLightGray, fontSize: "0.85em" }}>
            Speed Lightning Address
          </BlockyLightText>
          <div
            style={{
              display: "flex",
              alignItems: "stretch",
              marginTop: "6px",
              borderRadius: "6px",
              border: "1px solid rgba(255,255,255,0.12)",
              background: "rgba(255,255,255,0.05)",
              overflow: "hidden",
            }}
          >
            <input
              type="text"
              inputMode="email"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              value={lightningUsername}
              onChange={(e) => setLightningUsername(e.target.value)}
              placeholder="username"
              disabled={processing}
              aria-label="Speed Lightning username"
              style={{
                flex: 1,
                padding: "10px 12px",
                border: "none",
                background: "transparent",
                color: "white",
                fontSize: "14px",
                outline: "none",
              }}
            />
            <div
              style={{
                display: "flex",
                alignItems: "center",
                padding: "0 12px",
                background: "rgba(255,255,255,0.08)",
                color: sparketLightGray,
                fontSize: "14px",
                userSelect: "none",
                whiteSpace: "nowrap",
              }}
            >
              {SPEED_LN_SUFFIX}
            </div>
          </div>
          {cleanedUsername && (
            <div className="mt-1">
              <BlockyLightText style={{ color: sparketLightGray, fontSize: "0.75em" }}>
                Sending to: {manualAddress}
              </BlockyLightText>
            </div>
          )}
        </div>
      )}

      <WithdrawalSummaryBlock
        amount={amount}
        feeMode={WITHDRAWAL_FEE_MODE.DEDUCT}
        onWithdraw={handleWithdraw}
        disabled={disabled}
        processing={processing}
        summaryDisabled={!amount || amount < LIGHTNING_MIN_WITHDRAWAL}
        previewData={previewData}
        previewLoading={previewLoading}
        showFeeOptions={false}
      />
    </GreyModalSection>
  );
};

export default LightningWithdrawalBlock;
